import { IRynReplayEnvelope } from '@ups/xplat/web/core';

/**
 * One Identity envelope, ordered by capture time. The player uses this to show
 * who was signed in at the current playback instant — login mid-session and
 * anonymize() both change the answer as the scrub bar moves.
 */
export interface IRynReplayIdentitySnapshot {
  /** Epoch milliseconds UTC, matching the rrweb event clock. */
  ts: number;
  /**
   * True when this envelope is an anonymize() / signed-out stamp. Later
   * playback after this ts shows "Not signed in" until another identify.
   */
  anonymized: boolean;
  name?: string;
  email?: string;
  securityUserId?: string;
  /**
   * Auth0 user id from the recorder's Identity payload (#31332): the
   * Software Security user row's Auth0UserId, whatever login the app uses.
   * Never the Entra object id; empty for users with no Auth0 id on file.
   * Lets support tell apart two users with the same name.
   */
  auth0UserId?: string;
}

export interface IRynReplayIdentityLabel {
  /** Full name, email, or "Not signed in" / "Signed-in user". */
  name: string;
  /** Email when it adds information beyond `name`. */
  email?: string;
  /** Software Security user id when the label should copy on click. */
  copyUserId?: string;
  /** Auth0 user id offered for copy next to the user id (#31332). */
  copyAuth0UserId?: string;
}

/**
 * Session-row attribution from the read API when the event stream carried no
 * Identity envelopes (common on older recordings). The index Guid is a way to
 * FIND a session, not proof of who did anything — same caveat as the list row.
 */
export interface IRynReplaySessionAttribution {
  securityUserId?: string;
  /** Epoch milliseconds UTC, usually from X-Replay-Session-Started-At. */
  startedAtMs?: number;
}

const optionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
};

/**
 * Lift an Identity envelope into a snapshot. Payload is the recorder's
 * `IRynReplayIdentity`; top-level `anonymized` / `securityUserId` win because
 * ingest projection never opens Payload for those fields.
 */
export const readRynReplayIdentitySnapshot = (
  envelope: IRynReplayEnvelope
): IRynReplayIdentitySnapshot => {
  const payload = envelope.payload ?? {};
  const anonymized =
    envelope.anonymized === true || payload['anonymized'] === true;
  if (anonymized) {
    return { ts: envelope.ts, anonymized: true };
  }

  return {
    ts: envelope.ts,
    anonymized: false,
    name: optionalTrimmed(payload['name']),
    email: optionalTrimmed(payload['email']),
    securityUserId:
      optionalTrimmed(envelope.securityUserId) ??
      optionalTrimmed(payload['securityUserId']),
    auth0UserId: optionalTrimmed(payload['auth0UserId']),
  };
};

/**
 * Last identity stamp at or before `epochMs`. Snapshots must be in capture
 * order. Undefined means nothing has identified yet at that instant.
 */
export const identityAt = (
  snapshots: readonly IRynReplayIdentitySnapshot[],
  epochMs: number
): IRynReplayIdentitySnapshot | undefined => {
  if (!Number.isFinite(epochMs)) {
    return undefined;
  }
  let latest: IRynReplayIdentitySnapshot | undefined;
  for (const snap of snapshots) {
    if (snap.ts <= epochMs) {
      latest = snap;
    }
  }
  return latest;
};

/**
 * Player header copy for a snapshot. Unsigned, anonymized, and empty identity
 * stamps all say "Not signed in" — an empty label reads as a load failure.
 */
export const formatRynReplayIdentityLabel = (
  snap: IRynReplayIdentitySnapshot | undefined
): IRynReplayIdentityLabel => {
  if (!snap || snap.anonymized) {
    return { name: 'Not signed in' };
  }
  const label = formatSignedInLabel(snap);
  return snap.auth0UserId
    ? { ...label, copyAuth0UserId: snap.auth0UserId }
    : label;
};

const formatSignedInLabel = (
  snap: IRynReplayIdentitySnapshot
): IRynReplayIdentityLabel => {
  const name = snap.name?.trim();
  const email = snap.email?.trim();
  if (name && email && name !== email) {
    return { name, email, copyUserId: snap.securityUserId };
  }
  if (name) {
    return { name, copyUserId: snap.securityUserId };
  }
  if (email) {
    return { name: email, copyUserId: snap.securityUserId };
  }
  if (snap.securityUserId || snap.auth0UserId) {
    return { name: 'Signed-in user', copyUserId: snap.securityUserId };
  }
  return { name: 'Not signed in' };
};

/**
 * Ids on Identity stamps that have no display name yet. The player chrome asks
 * the host resolver for these; unnamed stamps are normal on older sessions.
 */
export const replayIdentityIdsMissingNames = (
  snapshots: readonly IRynReplayIdentitySnapshot[]
): string[] => [
  ...new Set(
    snapshots
      .filter((s) => !s.anonymized && s.securityUserId && !s.name)
      .map((s) => s.securityUserId as string)
  ),
];

/**
 * Overlay Software Security display names onto Identity stamps that only
 * carried a user id. Named stamps are left alone — the envelope is the source
 * of truth when the recorder already stamped a person.
 */
export const applyReplayIdentityDisplayNames = (
  snapshots: readonly IRynReplayIdentitySnapshot[],
  names: ReadonlyMap<string, { name: string; email?: string }>
): IRynReplayIdentitySnapshot[] =>
  snapshots.map((snap) => {
    if (snap.name || snap.anonymized || !snap.securityUserId) {
      return snap;
    }
    const display = names.get(snap.securityUserId);
    if (!display) {
      return snap;
    }
    return {
      ...snap,
      name: display.name,
      email: snap.email ?? display.email,
    };
  });

/**
 * The slice of a parsed stream Identity envelopes write. Kept structural so
 * this module does not import the stream parser (that file already imports us).
 */
export type IRynReplayIdentityStreamSink = {
  hasIdentity: boolean;
  identities: IRynReplayIdentitySnapshot[];
  appVersion?: string;
  buildNumber?: string;
};

const versionLabel = (value: unknown): string | undefined => {
  const trimmed = optionalTrimmed(value);
  return trimmed && trimmed.length <= 50 ? trimmed : undefined;
};

/**
 * Record one Identity envelope onto the stream: snapshot list, hasIdentity,
 * and version/build from the payload when the stream has not seen them yet.
 */
/**
 * Lift top-level `securityUserId` / `anonymized` from any non-Identity
 * envelope. Identity events use {@link recordRynReplayIdentityOnStream}; this
 * covers the common case where only ordinary event envelopes carry the Guid.
 */
export const recordEnvelopeAttributionOnStream = (
  stream: IRynReplayIdentityStreamSink,
  envelope: Pick<IRynReplayEnvelope, 'ts' | 'securityUserId' | 'anonymized'>
): void => {
  if (envelope.anonymized === true) {
    const last = stream.identities[stream.identities.length - 1];
    if (last?.anonymized) {
      return;
    }
    stream.hasIdentity = true;
    stream.identities.push({ ts: envelope.ts, anonymized: true });
    return;
  }

  const userId = optionalTrimmed(envelope.securityUserId);
  if (!userId) {
    return;
  }

  const last = stream.identities[stream.identities.length - 1];
  if (last && !last.anonymized && last.securityUserId === userId) {
    return;
  }

  stream.hasIdentity = true;
  stream.identities.push({
    ts: envelope.ts,
    anonymized: false,
    securityUserId: userId,
  });
};

/**
 * When the NDJSON stream never produced Identity snapshots, seed one from the
 * session index row the read API already validated.
 */
export const mergeSessionAttributionIntoIdentities = (
  identities: readonly IRynReplayIdentitySnapshot[],
  attribution: IRynReplaySessionAttribution | undefined
): IRynReplayIdentitySnapshot[] => {
  if (identities.length > 0) {
    return [...identities];
  }
  const userId = attribution?.securityUserId?.trim();
  const startMs = attribution?.startedAtMs;
  if (!userId || !Number.isFinite(startMs)) {
    return [...identities];
  }
  return [{ ts: startMs, anonymized: false, securityUserId: userId }];
};

/** Identity list ready for the player chrome after parse + session fallback. */
export const finalizeReplayIdentities = (
  identities: readonly IRynReplayIdentitySnapshot[],
  attribution: IRynReplaySessionAttribution | undefined
): IRynReplayIdentitySnapshot[] =>
  mergeSessionAttributionIntoIdentities(identities, attribution);

export const recordRynReplayIdentityOnStream = (
  stream: IRynReplayIdentityStreamSink,
  envelope: IRynReplayEnvelope
): void => {
  stream.hasIdentity = true;
  stream.identities.push(readRynReplayIdentitySnapshot(envelope));
  const payload = envelope.payload;
  if (!payload) {
    return;
  }
  if (!stream.appVersion) {
    stream.appVersion =
      versionLabel(payload['versionTag']) ?? stream.appVersion;
  }
  if (!stream.buildNumber) {
    stream.buildNumber =
      versionLabel(payload['buildNumber']) ?? stream.buildNumber;
  }
};
